import { GenerateSocialMediaRequest } from 'features/social-media/types';
import { UploadIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Label } from '../../../../components/ui/label';
import {
  RadioGroup,
  RadioGroupItem,
} from '../../../../components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select';
import { Button } from 'components/ui/button';
import { useSocialMediaContext } from 'features/social-media/SocialMediaContext';
import { Input } from 'components/ui/input';
import { useMutation } from '@tanstack/react-query';
import { socialMediaService } from '../../services/socialMediaCommonApi';
import toast from 'react-hot-toast';

interface PostEditPanelProps {
  onClose?: () => void;
  onGenerate?: (formData: GenerateSocialMediaRequest) => void;
  formOptions?: any;
}

export default function EditPanel({ formOptions }: PostEditPanelProps) {
  const [searchParams] = useSearchParams();

  const {
    isEditing,
    formData: savedData,
    setIsEditing,
    handleUpdateSocialMediaContent,
  } = useSocialMediaContext();

  const [fileName, setFileName] = useState<string | null>(null);
  const [files, setFiles] = useState<File | null>();

  const paramsId = searchParams.get('name');

  const [formData, setFormData] = useState({
    description: savedData?.description || '',
    toneOfVoice: savedData?.tone || '',
    language: savedData?.language || '',
    numDrafts: savedData?.drafts || 1,
    imageType: 'generate',
    post_topic: savedData?.topic || '',
    post_subtopic: savedData?.subTopic || '',
  });

  const [FilterFormOptions, setFilterFormOptions] = useState({
    topic:
      paramsId === 'linkedIn'
        ? formOptions?.data.platform_topics.LinkedInContent.topic
        : formOptions?.data.platform_topics.TwitterContent.topic,
    sub_topics:
      paramsId === 'linkedIn'
        ? formOptions?.data.platform_topics.LinkedInContent.sub_topics
        : formOptions?.data.platform_topics.TwitterContent.sub_topics,
    tones:
      paramsId === 'linkedIn'
        ? formOptions?.data.tones
        : formOptions?.data.tones,
    languages:
      paramsId === 'linkedin'
        ? formOptions?.data.languages
        : formOptions?.data.languages,
  });

  const generateMutation = useMutation({
    mutationKey: ['generate-social-media-post'],
    mutationFn: (data: GenerateSocialMediaRequest) => socialMediaService.generatePost(data),
    onSuccess: (response) => {
      handleUpdateSocialMediaContent(response.data);
      setIsEditing(false);
      toast.success('Content updated successfully');
    },
    onError: () => {
      toast.error('Failed to update content');
    },
  });

  const handleInputChange = (field: string, value: string | number) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setFiles(file);
    setFileName(file ? file.name : null);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const requestData: GenerateSocialMediaRequest = {
      query: formData.description,
      language: formData.language,
      post_topic: formData.post_topic,
      post_subtopic: formData.post_subtopic,
      platform_key: paramsId === 'linkedIn' ? 'LinkedInContent' : 'TwitterContent',
      tone_key: formData.toneOfVoice,
      number_of_posts: formData.numDrafts,
      img_required: formData.imageType === 'upload' ? false : true,
      number_of_words: 250,
    };

    generateMutation.mutate(requestData);
  };

  useEffect(() => {
    if (savedData) {
      setFormData({
        description: savedData.description || '',
        toneOfVoice: savedData.tone || '',
        language: savedData.language || '',
        numDrafts: savedData.drafts || 1,
        imageType: 'generate',
        post_topic: savedData.topic || '',
        post_subtopic: savedData.subTopic || '',
      });
    }
  }, [savedData]);

  useEffect(() => {
    if (formOptions) {
      setFilterFormOptions({
        topic:
          paramsId === 'linkedIn'
            ? formOptions?.data.platform_topics.LinkedInContent.topic
            : formOptions?.data.platform_topics.TwitterContent.topic,
        sub_topics:
          paramsId === 'linkedIn'
            ? formOptions?.data.platform_topics.LinkedInContent.sub_topics
            : formOptions?.data.platform_topics.TwitterContent.sub_topics,
        tones:
          paramsId === 'linkedIn'
            ? formOptions?.data.tones
            : formOptions?.data.tones,
        languages:
          paramsId === 'linkedIn'
            ? formOptions?.data.languages
            : formOptions?.data.languages,
      });
    }
  }, [formOptions, paramsId]);

  if (!isEditing) {
    return (
      <section className="bg-background text-text-primary w-[280px] flex flex-col gap-4 p-6 rounded-md h-full overflow-auto">
        <p className="uppercase text-sm">New Post</p>

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium">Topic</label>
          <Input disabled value={savedData?.topic} />
        </div>
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium">Sub-topic</label>
          <Input disabled value={savedData?.subTopic} />
        </div>
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium">Description</label>
          <Input disabled value={savedData?.description} />
        </div>
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium">Tone of voice</label>
          <Input disabled value={savedData?.tone} />
        </div>
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium">Language</label>
          <Input disabled value={savedData?.language} />
        </div>
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium">No. of Drafts</label>
          <Input disabled value={savedData?.drafts} />
        </div>
        {/* <div className="flex flex-col gap-2">
          <label className="text-sm font-medium">Image Type</label>
          <Input disabled />
        </div> */}
      </section>
    );
  }

  return (
    <section className="bg-background text-text-primary w-[280px] p-6 rounded-md h-full overflow-auto">
      <p className="uppercase text-sm">Edit Post</p>

      <form onSubmit={handleSubmit} className="flex flex-col h-full gap-4">
        {/* Topic - Disabled */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Topic</label>
          <Input
            value={formData.post_topic}
            disabled
            className="bg-background"
          />
        </div>

        {/* Sub-topic - Disabled */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Sub-topic</label>
          <Input
            value={formData.post_subtopic}
            disabled
            className="bg-background"
          />
        </div>

        {/* Description */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Description</label>
          <textarea
            className="w-full h-24 px-3 py-2 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-violet-500"
            placeholder="Enter Description"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
          />
        </div>

        {/* Tone of voice */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Tone of voice</label>
          <Select
            value={formData.toneOfVoice}
            onValueChange={(value) => handleInputChange('toneOfVoice', value)}
          >
            <SelectTrigger className="w-full bg-background border">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent className="h-[200px]">
              {FilterFormOptions.tones.map((option: string, index: number) => (
                <SelectItem key={index} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Language */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Language</label>
          <Select
            value={formData.language}
            onValueChange={(value) => handleInputChange('language', value)}
          >
            <SelectTrigger className="w-full bg-background border">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent className="h-[200px]">
              {FilterFormOptions.languages.map(
                (option: string, index: number) => (
                  <SelectItem key={index} value={option}>
                    {option}
                  </SelectItem>
                )
              )}
            </SelectContent>
          </Select>
        </div>

        {/* Image Type */}
        <div className="space-y-2">
          <label className="text-sm font-medium">IMAGE TYPE</label>
          <RadioGroup
            value={formData.imageType}
            onValueChange={(value) => handleInputChange('imageType', value)}
            className="flex flex-col gap-2"
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem value="generate" id="generate" />
              <Label htmlFor="generate">Generate Image</Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="upload" id="upload" />
              <Label htmlFor="upload">Upload Image</Label>
            </div>
          </RadioGroup>
        </div>

        {/* Image upload */}
        {formData.imageType == 'upload' && (
          <div className="flex flex-col relative items-center">
            <input
              type="file"
              id="image-upload"
              className="sr-only "
              accept="image/*"
              onChange={handleFileChange}
            />
            <label
              htmlFor="image-upload"
              className="py-2 cursor-pointer border border-dashed border-primary rounded-xl text-primaryflex items-center justify-center gap-2 w-full font-medium"
            >
              <UploadIcon size={18} />
              Upload
            </label>
            {fileName && (
              <p className="bg-gray-900 p-2 text-balance rounded-md w-full mt-2 text-sm text-white">
                {fileName}
              </p>
            )}
          </div>
        )}

        <div className="mt-auto flex mb-4 flex-col gap-3">
          <Button 
            variant={'customPrimary'} 
            size={'sm'}
            type="submit"
            disabled={generateMutation.isPending}
          >
            {generateMutation.isPending ? 'Applying...' : 'Apply'}
          </Button>
          <Button
            onClick={() => setIsEditing(false)}
            variant={'customSecondary'}
            type="button"
            size={'sm'}
          >
            Discard
          </Button>
        </div>
      </form>
    </section>
  );
}
